import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import MenuItem from "@mui/material/MenuItem"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { format, parseISO } from "date-fns"
import { useCallback, useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import SaveSessionNotesDialog from "../../users/SaveSessionNotesDialog"
import { buildActivityReportLink } from "../../users/activityReportLinks"
import { selectFullName, selectProfile } from "../../users/reducer"
import { formatTalkTime } from "../activity/formatTalkTime"
import SettingsSectionCard from "./SettingsSectionCard"

const DAY_RANGE_OPTIONS = [
  { value: 7, label: "Last 7 days" },
  { value: 14, label: "Last 14 days" },
  { value: 30, label: "Last 30 days" },
]

function formatSentAt(value) {
  if (!value) {
    return "Not sent"
  }
  try {
    return format(parseISO(value), "MMM d, h:mm a")
  } catch (_error) {
    return "Sent"
  }
}

function formatNotesSaved(value) {
  if (!value) {
    return "No notes saved"
  }
  try {
    return format(parseISO(value), "MMM d, h:mm a")
  } catch (_error) {
    return "Saved"
  }
}

export default function SessionTab() {
  const axios = useAuthorizedAxiosClient()
  const navigate = useNavigate()
  const profile = useSelector(selectProfile)
  const agentName = useSelector(selectFullName)
  const [days, setDays] = useState(7)
  const [loading, setLoading] = useState(true)
  const [records, setRecords] = useState([])
  const [modalReportDate, setModalReportDate] = useState(null)

  const loadRecords = useCallback(() => {
    setLoading(true)
    axios
      .get("/clients/api/concierge/daily-activity/", { params: { days } })
      .then(({ data }) => {
        setRecords(Array.isArray(data?.records) ? data.records : [])
      })
      .catch(() => setRecords([]))
      .finally(() => setLoading(false))
  }, [axios, days])

  useEffect(() => {
    loadRecords()
  }, [loadRecords])

  function handleCheckActivity(reportDate) {
    navigate(buildActivityReportLink(reportDate, profile?.id))
  }

  function handleModalSaved() {
    setModalReportDate(null)
    loadRecords()
  }

  return (
    <Column gap={3}>
      <SettingsSectionCard sx={{ gap: 2 }}>
        <Row
          sx={{
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Column gap={0.5}>
            <Typography variant="h6">Daily session history</Typography>
            <Typography variant="body2" color="text.secondary">
              Your saved activity for this client, grouped by day.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Agent: {agentName || "—"}
            </Typography>
          </Column>
          <TextField
            select
            size="small"
            label="Date range"
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            sx={{ minWidth: 180 }}
          >
            {DAY_RANGE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Row>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell align="right">Sessions</TableCell>
                <TableCell align="right">Active minutes</TableCell>
                <TableCell align="right">Calls</TableCell>
                <TableCell align="right">Talk Time</TableCell>
                <TableCell align="right">Connections</TableCell>
                <TableCell align="right">30+ sec</TableCell>
                <TableCell align="right">Bookings</TableCell>
                <TableCell align="right">Texts sent</TableCell>
                <TableCell>Notes saved</TableCell>
                <TableCell>Report sent</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.length ? (
                records.map((record) => {
                  const metrics = record.metrics || {}
                  return (
                    <TableRow key={record.report_date} hover>
                      <TableCell>{record.report_date_label}</TableCell>
                      <TableCell align="right">
                        {metrics.total_dialer_sessions ?? 0}
                      </TableCell>
                      <TableCell align="right">
                        {metrics.active_minutes ?? 0}
                      </TableCell>
                      <TableCell align="right">
                        {metrics.total_calls ?? 0}
                      </TableCell>
                      <TableCell align="right">
                        {formatTalkTime(metrics.total_talk_seconds)}
                      </TableCell>
                      <TableCell align="right">
                        {metrics.total_connections ?? 0}
                      </TableCell>
                      <TableCell align="right">
                        {metrics.conversations_30_seconds ?? 0}
                      </TableCell>
                      <TableCell align="right">
                        {metrics.total_bookings ?? 0}
                      </TableCell>
                      <TableCell align="right">
                        {metrics.texts_sent ?? 0}
                      </TableCell>
                      <TableCell>
                        {formatNotesSaved(record.notes_updated_at)}
                      </TableCell>
                      <TableCell>{formatSentAt(record.last_sent_at)}</TableCell>
                      <TableCell align="right">
                        <Row gap={1} sx={{ justifyContent: "flex-end" }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              handleCheckActivity(record.report_date)
                            }
                          >
                            Check activity
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={Boolean(modalReportDate)}
                            onClick={() =>
                              setModalReportDate(record.report_date)
                            }
                          >
                            Save notes
                          </Button>
                        </Row>
                      </TableCell>
                    </TableRow>
                  )
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Typography variant="body2" color="text.secondary">
                      No saved activity for this range yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        <Alert severity="info" icon={<InfoOutlinedIcon fontSize="inherit" />}>
          Active minutes count time you interact with OneBase while working for
          this client (tab visible, activity within the last 5 minutes). Save
          your session notes here; reports email the parent automatically each
          night from signature@smartsetter.io.
        </Alert>
      </SettingsSectionCard>

      {modalReportDate ? (
        <SaveSessionNotesDialog
          open
          axios={axios}
          reportDate={modalReportDate}
          onSaved={handleModalSaved}
          onClose={() => setModalReportDate(null)}
        />
      ) : null}
    </Column>
  )
}
